import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  List, 
  ListItem, 
  ListItemText, 
  LinearProgress, 
  Typography, 
  Box,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Alert
} from '@mui/material';
import { 
  useRecordContext, 
  useDataProvider, 
  useNotify, 
  useRefresh,
  useGetList,
  useGetOne,
  UpdateParams
} from 'react-admin';
import { Funeral } from '../types';
import { SP3DataViewer } from './SP3DataViewer';
import { supabase } from '../lib/supabaseClient';
import { getToken } from '../authentication/authProvider';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CppBenefitsMailingModal from './CppBenefitsMailingModal';
import { fallbackFilename, saveBlob } from '../lib/download';

const FuneralFileUploader = () => {
  const record = useRecordContext<Funeral>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [files, setFiles] = useState<Array<{ title: string; path: string; isSP3?: boolean }>>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasSP3, setHasSP3] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [isSP3Upload, setIsSP3Upload] = useState(false);
  const [signedContracts, setSignedContracts] = useState<string[]>([]);
  const [cppBenefitsDocuments, setCppBenefitsDocuments] = useState<string[]>([]);
  const [cppBenefitsMailedAt, setCppBenefitsMailedAt] = useState<string | null>(null);
  const [cppBenefitsMailingStatus, setCppBenefitsMailingStatus] = useState<string | null>(null);
  const [mailingModalOpen, setMailingModalOpen] = useState(false);
  const [parsingProgress, setParsingProgress] = useState(false);
  const [showSp3Viewer, setShowSp3Viewer] = useState(false);

  const { data: existingFiles, isLoading } = useGetList('attachments', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'id', order: 'DESC' },
    filter: { funeral_id: record?.id },
  });

  // Fetch deceased client for modal
  const { data: deceasedClient } = useGetOne(
    'clients',
    { id: record?.deceased_client_id || 0 },
    { enabled: !!record?.deceased_client_id }
  );

  // Fetch paying client for modal
  const { data: payingClient } = useGetOne(
    'clients',
    { id: record?.paying_client_id || 0 },
    { enabled: !!record?.paying_client_id }
  );

  useEffect(() => {
    if (existingFiles) {
      const filesList = existingFiles.map(file => ({ 
        title: file.title, 
        path: file.path,
        isSP3: file.document_type === 'SP3'
      }));
      
      setFiles(filesList);
      // Check if any file is marked as SP3
      setHasSP3(filesList.some(file => file.isSP3 || file.title.toLowerCase().startsWith('sp3')));
    }

    // Set signed contracts from the record
    if (record?.contracts_signed_urls && Array.isArray(record.contracts_signed_urls)) {
      setSignedContracts(record.contracts_signed_urls);
    }

    // Set CPP Benefits documents from the record
    if (record?.cpp_benefits_signed_urls && Array.isArray(record.cpp_benefits_signed_urls)) {
      setCppBenefitsDocuments(record.cpp_benefits_signed_urls);
    }
    
    // Set CPP Benefits mailing status
    if (record?.cpp_benefits_mailed_at) {
      setCppBenefitsMailedAt(record.cpp_benefits_mailed_at);
    }
    
    if (record?.cpp_benefits_mailing_status) {
      setCppBenefitsMailingStatus(record.cpp_benefits_mailing_status);
    }
  }, [existingFiles, record]);

  const uploadFile = async (file: File, isSP3Flag: boolean) => {
    try {
      const formData = new FormData();
      formData.append('files', file);

      const fileObj = {
        src: URL.createObjectURL(file),
        title: file.name,
        document_type: isSP3Flag ? 'SP3' : null,
        funeral_id: record.id
      };

      const updateParams: UpdateParams = {
        id: record.id,
        data: {
          id: record.id,
          files: fileObj
        },
        previousData: record
      };

      const response = await dataProvider.update('funerals', updateParams);

      if (response.data) {
        setFiles((prevFiles) => [
          ...prevFiles, 
          { 
            title: file.name, 
            path: `funerals/${record.id}/${file.name}`,
            isSP3: isSP3Flag
          }
        ]);
        
        if (isSP3Flag) {
          setHasSP3(true);
          notify('SP3 file uploaded successfully', { type: 'success' });
          
          // Wait a moment for the database transaction to complete, then parse
          // This ensures the attachment record is fully committed before we try to query it
          setTimeout(async () => {
            await parseSP3File();
          }, 1500);
        } else {
          notify('File uploaded successfully', { type: 'success' });
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      notify('Error uploading file', { type: 'error' });
    }
  };

  const parseSP3File = async () => {
    try {
      setParsingProgress(true);
      notify('Parsing SP3 file...', { type: 'info' });

      // Get the authentication token
      const token = await getToken();
      
      // Call the NestJS backend to process all SP3 files for this funeral
      const nestjsUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/';
      const response = await fetch(`${nestjsUrl}sp3/process/funeral/${record.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || 'SP3 parsing failed');
      }

      const results = await response.json();
      
      // Find the first successful result
      const successfulResult = results.find((r: any) => r.success);
      
      if (!successfulResult) {
        const errors = results.map((r: any) => r.error).filter(Boolean);
        throw new Error(errors.join(', ') || 'SP3 parsing failed');
      }

      notify('SP3 file parsed successfully!', { type: 'success' });
      
      // Refresh the funeral data to get the updated sp3_data
      refresh();
      
      // Show the comparison modal after a short delay to allow data to refresh
      setTimeout(() => {
        setShowSp3Viewer(true);
      }, 500);
      
    } catch (error) {
      console.error('Error parsing SP3:', error);
      notify(`Failed to parse SP3: ${(error as Error).message}`, { type: 'error' });
    } finally {
      setParsingProgress(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!record || !record.id) {
      notify('Error: Funeral record not found', { type: 'error' });
      return;
    }

    // Handle multiple files sequentially
    setUploading(true);
    setProgress(0);

    for (const file of acceptedFiles) {
      // Check if filename starts with SP3
      const isPossibleSP3 = file.name.toLowerCase().startsWith('sp3');

      // Always ask — either to confirm SP3 or to offer replacing the existing one
      setCurrentFile(file);
      setIsSP3Upload(isPossibleSP3); // Pre-select based on filename
      setDialogOpen(true);

      // Wait for dialog interaction before proceeding
      break;
    }

    setUploading(false);
    refresh();
  }, [record, dataProvider, notify, refresh, hasSP3]);

  const handleDialogClose = async (confirmSP3: boolean) => {
    setDialogOpen(false);
    
    if (currentFile) {
      await uploadFile(currentFile, confirmSP3);
      setCurrentFile(null);
      
      // Process any remaining files if there were multiple
      // This would need to be expanded for a complete implementation
      setProgress(100);
      setUploading(false);
      refresh();
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleDownload = async (filePath: string, title?: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('attachments')
        .createSignedUrl(filePath, 60);

      if (error) {
        notify('Error generating download link', { type: 'error' });
        console.error('Error generating download link:', error);
        return;
      }

      const response = await fetch(data.signedUrl as string);
      const blob = await response.blob();
      // Save under the file's title — a blob: tab saved as a UUID (T-173).
      saveBlob(blob, fallbackFilename({ original_filename: title, storage_path: filePath }));
    } catch (error) {
      console.error('Error downloading file:', error);
      notify('Error downloading file', { type: 'error' });
    }
  };

  // Helper function to extract filename from URL
  const getFilenameFromUrl = (url: string): string => {
    try {
      const pathname = new URL(url).pathname;
      const filename = pathname.split('/').pop() || 'Unknown file';
      // URL decode the filename to handle any encoded characters
      return decodeURIComponent(filename);
    } catch (error) {
      // If URL parsing fails, try to extract the filename from the end of the string
      const parts = url.split('/');
      return parts[parts.length - 1] || 'Unknown file';
    }
  };

  // console.log('Funeral Record:', record);
  // console.log('SP3 Data:', record?.sp3_data);

  if (isLoading) return <Typography>Loading...</Typography>;

  if (!record || !record.id) {
    return <Typography>Error: Funeral record not found</Typography>;
  }

  const hasSignedContracts = signedContracts.length > 0;

  return (
    <Box>
      {/* SP3 Status Indicator */}
      <Box sx={{ mb: 2 }}>
        {hasSP3 ? (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            SP3 file has been uploaded
          </Alert>
        ) : (
          <Alert severity="warning" icon={<ErrorIcon />}>
            SP3 file has not been uploaded yet
          </Alert>
        )}
      </Box>

      {/* Signed Contracts Section */}
      {hasSignedContracts && (
        <>
          <Box sx={{ mb: 3 }}>
            <Alert 
              severity="info" 
              icon={<ArticleIcon />}
              sx={{ mb: 2 }}
            >
              <Typography>Signed Contracts ({signedContracts.length})</Typography>
            </Alert>
            
            {signedContracts.map((url, index) => (
              <Box 
                key={`signed-${index}`}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  px: 1,
                  mb: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <DescriptionIcon 
                    sx={{ 
                      mr: 2, 
                      color: '#5c68e0', 
                      fontSize: 32,
                      mt: 0.5
                    }} 
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {getFilenameFromUrl(url)
                        .replace(/[_-]/g, ' ')
                        .replace('.pdf', '')
                        .replace(/contract|submission|document/gi, '')
                        .trim()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Signed: {record.contracts_signed_at 
                        ? new Date(record.contracts_signed_at).toLocaleDateString('en-US', {
                            month: 'numeric',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : 'Unknown date'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip 
                    label="Signed" 
                    sx={{ 
                      mr: 2,
                      bgcolor: '#4c7a4c', 
                      color: 'white',
                      borderRadius: 4,
                      fontWeight: 'medium'
                    }}
                  />
                  <Button 
                    variant="outlined"
                    sx={{ 
                      borderRadius: 4, 
                      borderColor: '#5c68e0', 
                      color: '#5c68e0',
                      px: 3
                    }}
                    onClick={() => window.open(url, '_blank')}
                  >
                    VIEW
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
          <Divider sx={{ my: 3 }} />
        </>
      )}

      {/* CPP Benefits Documents Section */}
      {cppBenefitsDocuments.length > 0 && (
        <>
          <Box sx={{ mb: 3 }}>
            <Alert 
              severity="info" 
              icon={<ArticleIcon />}
              sx={{ mb: 2 }}
            >
              <Typography>CPP Benefits Documents ({cppBenefitsDocuments.length})</Typography>
            </Alert>
            
            {cppBenefitsDocuments.map((url, index) => (
              <Box 
                key={`cpp-benefits-${index}`}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  px: 1,
                  mb: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  flexWrap: 'wrap',
                  gap: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 0, flex: '1 1 auto' }}>
                  <DescriptionIcon 
                    sx={{ 
                      mr: 2, 
                      color: '#5c68e0', 
                      fontSize: 32,
                      mt: 0.5,
                      flexShrink: 0
                    }} 
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      CPP Benefits Application
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Signed: {record.cpp_benefits_signed_at 
                        ? new Date(record.cpp_benefits_signed_at).toLocaleDateString('en-US', {
                            month: 'numeric',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : 'Unknown date'}
                    </Typography>
                    {cppBenefitsMailedAt && (
                      <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                        ✓ Mailed: {new Date(cppBenefitsMailedAt).toLocaleDateString('en-US', {
                          month: 'numeric',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
                  <Chip 
                    label="Signed" 
                    sx={{ 
                      bgcolor: '#4c7a4c', 
                      color: 'white',
                      borderRadius: 4,
                      fontWeight: 'medium'
                    }}
                  />
                  {cppBenefitsMailingStatus === 'sent' && (
                    <Chip 
                      icon={<CheckCircleIcon sx={{ color: 'white !important' }} />}
                      label="Mailed" 
                      sx={{ 
                        bgcolor: '#4caf50', 
                        color: 'white',
                        borderRadius: 4,
                        fontWeight: 'medium'
                      }}
                    />
                  )}
                  <Button 
                    variant="outlined"
                    size="small"
                    sx={{ 
                      borderRadius: 4, 
                      borderColor: '#5c68e0', 
                      color: '#5c68e0',
                      px: 2
                    }}
                    onClick={() => window.open(url, '_blank')}
                  >
                    VIEW
                  </Button>
                  <Button 
                    variant="outlined"
                    size="small"
                    startIcon={<MailOutlineIcon />}
                    sx={{ 
                      borderRadius: 4, 
                      borderColor: cppBenefitsMailingStatus === 'sent' ? '#4caf50' : '#ff9800',
                      color: cppBenefitsMailingStatus === 'sent' ? '#4caf50' : '#ff9800',
                      px: 2
                    }}
                    onClick={() => setMailingModalOpen(true)}
                  >
                    {cppBenefitsMailingStatus === 'sent' ? 'SEND' : 'MAIL'}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
          <Divider sx={{ my: 3 }} />
        </>
      )}

      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed #cccccc',
          borderRadius: '4px',
          padding: '20px',
          textAlign: 'center',
          cursor: 'pointer',
          marginBottom: '20px',
          borderColor: hasSP3 ? '#cccccc' : '#ff9800',
        }}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <Typography>Drop the files here ...</Typography>
        ) : (
          <Typography>
            {hasSP3 
              ? "Drag 'n' drop some files here, or click to select files" 
              : "Drag 'n' drop the SP3 file here, or click to select files"}
          </Typography>
        )}
      </Box>

      {uploading && (
        <Box sx={{ width: '100%', marginBottom: '20px' }}>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}

      <List>
        {files.map((file, index) => (
          <ListItem key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ListItemText primary={file.title} />
              {file.isSP3 && (
                <Chip 
                  label="SP3" 
                  color="primary" 
                  size="small" 
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => handleDownload(file.path, file.title)}
            >
              Download
            </Button>
          </ListItem>
        ))}
      </List>

      {/* SP3 Selection Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => handleDialogClose(false)}
      >
        <DialogTitle>{hasSP3 ? 'Replace SP3 file?' : 'Is this an SP3 file?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {currentFile?.name}
            {hasSP3
              ? ' — An SP3 file already exists. Would you like to replace it with this file?'
              : isSP3Upload
                ? ' appears to be an SP3 file based on its name.'
                : ' — Is this the SP3 form for this funeral?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDialogClose(false)}>
            {hasSP3 ? 'Upload as regular file' : 'No'}
          </Button>
          <Button onClick={() => handleDialogClose(true)} variant="contained" autoFocus>
            {hasSP3 ? 'Yes, replace SP3' : 'Yes, this is the SP3'}
          </Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ my: 2 }} />
      
      {parsingProgress && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Parsing SP3 file... Please wait.
        </Alert>
      )}
      
      {record && record.sp3_data && (
        <>
          <SP3DataViewer 
            sp3Data={record.sp3_data} 
            autoOpen={showSp3Viewer}
            onClose={() => setShowSp3Viewer(false)}
          />
          <pre style={{ display: 'none' }}>
            {JSON.stringify(record.sp3_data, null, 2)}
          </pre>
        </>
      )}

      {/* CPP Benefits Mailing Modal */}
      <CppBenefitsMailingModal
        open={mailingModalOpen}
        onClose={() => setMailingModalOpen(false)}
        funeralUuid={record?.uuid || ''}
        funeralId={Number(record?.id) || 0}
        deceasedName={deceasedClient ? 
          `${deceasedClient.first_name || ''} ${deceasedClient.last_name || ''}`.trim() : 
          'Unknown'
        }
        deceasedProvince={deceasedClient?.province}
        deceasedPostalCode={deceasedClient?.postal_code}
        deceasedAddress={deceasedClient ? 
          [
            deceasedClient.apartment ? `${deceasedClient.apartment}-` : '',
            deceasedClient.civic_number || '',
            deceasedClient.address || '',
            deceasedClient.city || '',
            deceasedClient.province || ''
          ].filter(Boolean).join(' ').trim() : 
          ''
        }
        buyerName={payingClient ? 
          `${payingClient.first_name || ''} ${payingClient.last_name || ''}`.trim() : 
          'Unknown'
        }
        buyerRelationship={record?.paying_client_relationship}
        onSuccess={() => {
          refresh();
        }}
      />
    </Box>
  );
};

export default FuneralFileUploader;